import React, { useCallback, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';
import moment from 'moment-timezone';

import WpToastManager from 'Wpds/components/toast/WpToastManager';
import StyledSentence from 'Shared/components/StyledSentence/StyledSentence';
import StyledTitle from 'Shared/components/StyledTitle/StyledTitle';
import CharmaLogo from 'Shared/components/Logos/CharmaLogo';
import TextField from 'Shared/components/TextField/TextField';
import FieldGroup from 'Shared/components/FormComponents/FieldGroup/FieldGroup';
import { updateProfile } from 'Shared/actions/user';
import { handleRequestError } from 'Shared/utils/handle-request-error';
import { isEmptyString } from 'Shared/helpers';

import './OnboardingWelcome.scss';
import SignUpButton from './SignUpButton/SignUpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

const roleOptions = [
  { label: 'Individual Contributor', value: 'full_member' },
  { label: 'Manager', value: 'manager' },
];

const reportOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19].map(el => {
  return { label: String(el), value: String(el) };
});
reportOptions.push({ label: '20 or more', value: '20 or more' });

const customStyles = {
  control: base => ({ ...base, fontSize: '14px' }),
  menuList: base => ({ ...base, fontSize: '14px' }),
  menu: base => ({ ...base, bottom: '100%', top: 'auto' }),
};

function valuesDecorator({ id, complete_onboarding_step, firstName, lastName, role, numDirectReports, timezone }) {
  return {
    id,
    complete_onboarding_step,
    role: role.value,
    num_direct_reports: numDirectReports ? numDirectReports.value : null,
    first_name: firstName,
    last_name: lastName,
    selected_time_zone_option: timezone,
  };
}

function OnboardingWelcome() {
  const { user } = useCurrentUser();
  const dispatch = useDispatch();

  const timezoneOptions = useSelector(state => state.default_options.time_zone_options);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formValues, setFormValues] = useState({
    id: user.id,
    firstName: user.first_name,
    lastName: user.last_name,
    role: roleOptions.find(({ value }) => value === (user.role || 'full_member')) || roleOptions[0],
    numDirectReports: reportOptions.find(({ value }) => value === user.numDirectReports),
    timezone:
      timezoneOptions.find(option => option.value === moment.tz.guess()) ||
      timezoneOptions.find(option => option.value === 'America/Los_Angeles') ||
      timezoneOptions[0],
    complete_onboarding_step: 'finish',
  });

  const handleTextFieldChange = useCallback(
    event => setFormValues({ ...formValues, [event.target.name]: event.target.value }),
    [formValues]
  );

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const response = await handleRequestError(
        dispatch(updateProfile(valuesDecorator(formValues), { supressSuccessMessage: true }))
      );

      window.analytics.track('Completed Onboarding (frontend)');
      window.location.href = response.data.redirect_path;
      return response;
    } catch (e) {
      setIsSubmitting(false);
      WpToastManager.danger(e.message);
      return e;
    }
  }, [dispatch, formValues]);

  const handleRoleChange = useCallback(value => setFormValues({ ...formValues, role: value }), [formValues]);
  const handleNumDirectReportsChange = useCallback(value => setFormValues({ ...formValues, numDirectReports: value }), [
    formValues,
  ]);
  const handleTimezoneChange = useCallback(value => setFormValues({ ...formValues, timezone: value }), [formValues]);

  const isSubmitDisabled = useMemo(
    () =>
      isEmptyString(formValues.firstName) ||
      isEmptyString(formValues.lastName) ||
      isEmptyString(formValues.role.value) ||
      (formValues.role.value === 'manager' && !formValues.numDirectReports) ||
      (formValues.role.value === 'manager' &&
        formValues.numDirectReports &&
        isEmptyString(formValues.numDirectReports.value)) ||
      isEmptyString(formValues.timezone.value) ||
      isSubmitting,
    [
      formValues.firstName,
      formValues.lastName,
      formValues.role.value,
      formValues.numDirectReports,
      formValues.timezone.value,
      isSubmitting,
    ]
  );

  return (
    <div className="card manager-onboarding-welcome h-90 m-auto">
      <div className="card-body d-flex flex-column justify-content-center align-items-center">
        <CharmaLogo type="icon" width="50" className="mx-auto" />
        <StyledTitle>Welcome to PI Perform!</StyledTitle>
        <div className="d-flex flex-column align-items-center p-3 w-100">
          <StyledSentence>
            Your place for one-on-one’s and easy management. Please complete the info below to complete sign up.
          </StyledSentence>
          <form>
            <div className="d-flex align-items-end">
              <FieldGroup label="Your First Name" className="mr-1 w-100">
                <TextField
                  name="firstName"
                  type="text"
                  placeholder="First Name"
                  value={formValues.firstName}
                  onChange={handleTextFieldChange}
                />
              </FieldGroup>
              <FieldGroup label="Your Last Name" className="w-100">
                <TextField
                  name="lastName"
                  type="text"
                  placeholder="Last Name"
                  value={formValues.lastName}
                  onChange={handleTextFieldChange}
                />
              </FieldGroup>
            </div>
            <FieldGroup label="Your Role">
              <Select
                value={formValues.role}
                options={roleOptions}
                components={{
                  DropdownIndicator: () => (
                    <span style={{ padding: '5px 10px 10px' }}>
                      <WpIcon name="sort-down" />
                    </span>
                  ),
                  IndicatorSeparator: null,
                }}
                styles={customStyles}
                onChange={handleRoleChange}
              />
            </FieldGroup>

            {formValues.role.value === 'manager' ? (
              <FieldGroup label="Number of direct reports">
                <Select
                  value={formValues.numDirectReports}
                  options={reportOptions}
                  components={{
                    DropdownIndicator: () => (
                      <span style={{ padding: '5px 10px 10px' }}>
                        <WpIcon name="sort-down" />
                      </span>
                    ),
                    IndicatorSeparator: null,
                  }}
                  styles={customStyles}
                  onChange={handleNumDirectReportsChange}
                />
              </FieldGroup>
            ) : null}

            <FieldGroup label="Time Zone">
              <Select
                value={formValues.timezone}
                options={timezoneOptions}
                id="onboarding-welcome-select-timezone"
                components={{
                  DropdownIndicator: () => (
                    <span style={{ padding: '5px 10px 10px' }}>
                      <WpIcon name="sort-down" />
                    </span>
                  ),
                  IndicatorSeparator: null,
                }}
                styles={customStyles}
                onChange={handleTimezoneChange}
              />
            </FieldGroup>
            <SignUpButton isSubmitting={isSubmitting} onSubmit={handleSubmit} disabled={isSubmitDisabled} />
          </form>
        </div>
      </div>
    </div>
  );
}

export default OnboardingWelcome;
